import { motion } from "motion/react";
import { panelClassName } from "@/lib/constants";
import {
  formatClock,
  formatDateLabel,
  formatTimeZoneName,
  formatUtcOffset,
  getBrowserTimeZone,
} from "@/lib/formatting";
import { useNow } from "@/lib/hooks/use-now";
import { groupLocationsByTimeZone } from "@/lib/locations";
import type { PrayerLocation, PrayerSnapshot } from "@/types/prayer";

type TimezonePanelProps = {
  location: PrayerLocation | null;
  snapshot: PrayerSnapshot | null;
};

const timezoneGroups = groupLocationsByTimeZone();

const TimezoneFactCard = ({
  eyebrow,
  now,
  timeZone,
  valueClassName,
}: {
  eyebrow: string;
  now: Date;
  timeZone: string;
  valueClassName?: string;
}) => (
  <div className="rounded-2xl border border-zinc-800/60 bg-zinc-900/60 p-4">
    <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">{eyebrow}</p>
    <p className={`mt-2 text-lg font-semibold ${valueClassName ?? "text-zinc-100"}`}>{formatClock(now, timeZone)}</p>
    <p className="mt-1 text-xs text-zinc-400">{formatDateLabel(now, timeZone)}</p>
    <div className="mt-3 flex flex-wrap gap-2 text-[10px]">
      <span className="rounded-full border border-zinc-800 bg-zinc-950/70 px-2 py-1 font-medium text-zinc-300">
        {timeZone}
      </span>
      <span className="rounded-full border border-zinc-800 bg-zinc-950/70 px-2 py-1 font-medium text-zinc-500">
        {formatUtcOffset(now, timeZone) ?? "UTC"}
      </span>
    </div>
  </div>
);

const TimezonePanel = ({ location, snapshot }: TimezonePanelProps) => {
  const now = useNow();
  const browserTimeZone = getBrowserTimeZone();

  return (
    <motion.div
      animate={{ opacity: 1, x: 0 }}
      className={panelClassName}
      initial={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.4 }}
    >
      <div className="shrink-0 border-b border-zinc-800/50 px-5 pt-5 pb-4">
        <h2 className="text-lg font-semibold text-zinc-100">Timezone Intelligence</h2>
        <p className="mt-1 text-xs text-zinc-500">
          Every marker embeds its own IANA timezone so all prayer times stay location-correct without a backend.
        </p>
      </div>

      <div className="shrink-0 border-b border-zinc-800/50 px-5 py-4">
        {!location || !snapshot ? (
          <div className="rounded-2xl border border-zinc-800/60 bg-zinc-900/60 px-4 py-3 text-sm text-zinc-400">
            Select a city to inspect its embedded timezone against your browser and UTC.
          </div>
        ) : (
          <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full border border-emerald-500/20 bg-emerald-500/10 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-400">
                Selected Zone
              </span>
              <span className="rounded-full border border-zinc-800 bg-zinc-950/70 px-2 py-1 text-[10px] font-medium text-zinc-300">
                {snapshot.timezoneLabel}
              </span>
            </div>
            <p className="mt-3 text-lg font-semibold text-zinc-100">{location.city}</p>
            <p className="mt-1 text-sm text-zinc-400">{location.timeZone}</p>
            <p className="mt-2 text-xs text-zinc-500">
              {formatTimeZoneName(now, location.timeZone) ?? "Local time zone"}
            </p>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <TimezoneFactCard
            eyebrow={location ? `${location.city} Local` : "Selected Local"}
            now={now}
            timeZone={location?.timeZone ?? "UTC"}
            valueClassName="text-emerald-300"
          />
          <TimezoneFactCard eyebrow="Browser Zone" now={now} timeZone={browserTimeZone} valueClassName="text-sky-300" />
          <TimezoneFactCard eyebrow="UTC Reference" now={now} timeZone="UTC" valueClassName="text-zinc-100" />
        </div>

        <div className="mt-4 rounded-2xl border border-zinc-800/60 bg-zinc-900/50 p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Curated Zone Map</p>
              <p className="mt-1 text-xs text-zinc-400">Distinct timezone buckets represented by the globe markers.</p>
            </div>
            <span className="rounded-full border border-zinc-800 bg-zinc-950/70 px-2 py-1 text-[10px] font-semibold text-zinc-400">
              {timezoneGroups.length} zones
            </span>
          </div>

          <div className="mt-4 grid gap-2">
            {timezoneGroups.map((group) => (
              <div
                className={`rounded-xl border px-3 py-3 ${
                  location?.timeZone === group.timeZone
                    ? "border-emerald-500/30 bg-emerald-500/5"
                    : "border-zinc-800/60 bg-zinc-950/50"
                }`}
                key={group.timeZone}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-semibold text-zinc-100">{group.timeZone}</p>
                    <p className="mt-1 text-xs text-zinc-500">
                      {group.locations.map((groupLocation) => groupLocation.city).join(" · ")}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-medium text-zinc-300">{formatUtcOffset(now, group.timeZone) ?? "UTC"}</p>
                    <p className="text-[10px] text-zinc-500">{group.locations.length} marker(s)</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default TimezonePanel;
